import { FunctionComponent, MouseEvent, memo, useCallback, useContext } from 'react';
import { UserContext } from '../../../../context';
import { useOrganisationTagsExternalQuery } from '../../../../services';
import { ORG_TAGS_SIZE_LIMIT } from '../../../constants';
import { useFollowableTags, useIsFollowAllowed, useTagFollow } from '../../../hooks';
import { isAdminLayout } from '../../../utils';
import { FollowToggle } from '../../FollowToggle';
import { NotificationToggle } from '../../NotificationToggle';
import { useTagNotification } from './useTagNotification';

interface TagFollowControlsProps {
  tagId: number;
}

export const TagFollowControls: FunctionComponent<TagFollowControlsProps> = memo(({ tagId }) => {
  const { isAnonymous } = useContext(UserContext).userProfile;

  const isFollowAllowed = useIsFollowAllowed();

  const { followableTags } = useFollowableTags();

  const { data } = useOrganisationTagsExternalQuery(
    { size: ORG_TAGS_SIZE_LIMIT },
    { skip: isAnonymous || isAdminLayout() }
  );

  const { isTagFollowed, toggleTagFollow, isFollowPending } = useTagFollow();

  const { isTagNotified, setTagNotified, toggleTagNotification, isNotificationPending } =
    useTagNotification();

  const followableTag = followableTags.find(({ id }) => id === tagId);

  const tag = data?.items.find(({ id }) => id === tagId);

  const followHandler = useCallback(
    async (event: MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();

      if (!followableTag) {
        return;
      }

      const isNotified = isTagNotified(followableTag);

      setTagNotified({ tagId, isNotified: !isTagFollowed(followableTag) });

      if (await toggleTagFollow(followableTag)) {
        return;
      }

      setTagNotified({ tagId, isNotified });
    },
    [followableTag, isTagFollowed, isTagNotified, setTagNotified, tagId, toggleTagFollow]
  );

  const notificationHandler = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();

      if (tag) {
        toggleTagNotification(tag);
      }
    },
    [tag, toggleTagNotification]
  );

  return (
    <>
      {isFollowAllowed && followableTag && (
        <FollowToggle
          followed={isTagFollowed(followableTag)}
          isPending={isFollowPending}
          onClick={followHandler}
          hideBorder
        />
      )}
      {!isAnonymous && tag && (
        <NotificationToggle
          isEnabled={isTagNotified(tag)}
          isPending={isNotificationPending}
          onClick={notificationHandler}
        />
      )}
    </>
  );
});
